/**
 * WASM 管理器
 */

// 声明全局类型
declare global {
  interface Window {
    Go;
    signParams: (params, path: string, timestamp: string) => string;
    wasmLoadPromise?: Promise<void>;
    __wasmPreloadTiming?: Record<string, number> | null;
  }
}

export interface WasmConfig {
  wasmUrl: string; // WASM 文件的 URL 路径
}

export default class WasmManager {
  private static _instance: WasmManager | null = null;
  // 标记WASM是否已加载完成
  private _isWasmLoaded: boolean = false;
  private _config: WasmConfig;
  private _loadPromise: Promise<void> | null = null;

  public static get instance(): WasmManager {
    if (this._instance == null) {
      this._instance = new WasmManager();
    }
    return this._instance;
  }

  constructor() {
    // 默认配置
    this._config = {
      wasmUrl: "/assets/wasm/main.wasm",
    };
  }

  /**
   * 初始化 WASM
   * @param config 可选的配置覆盖
   */
  async initWasm(config?: Partial<WasmConfig>): Promise<void> {
    // 合并配置
    this._config = { ...this._config, ...config };

    // 如果已经加载完成，直接返回
    if (this._isWasmLoaded) return;

    // 如果正在加载中，返回现有的Promise
    if (this._loadPromise) {
      return this._loadPromise;
    }

    // 检查是否有预加载的Promise
    if (window.wasmLoadPromise) {
      try {
        await window.wasmLoadPromise;
        this._isWasmLoaded = true;
        console.log("WASM 已通过预加载完成");
        if (window.__wasmPreloadTiming) {
          console.log("[WASM][preload] timing(ms):", window.__wasmPreloadTiming);
        }
        return;
      } catch (error) {
        console.warn("预加载失败，尝试重新加载:", error);
        // 继续执行正常加载流程
      }
    }

    // 开始正常加载流程
    this._loadPromise = this._loadWasm();

    try {
      await this._loadPromise;
    } finally {
      this._loadPromise = null;
    }
  }

  /**
   * 实际加载WASM文件
   */
  private async _loadWasm(): Promise<void> {
    if (this._isWasmLoaded) return;

    if (window["Go"] === undefined) {
      throw new Error("Go environment not loaded");
    }

    const startAt = Date.now();
    const go = new window["Go"]();
    const fetchStartAt = Date.now();
    const response = await fetch(this._config.wasmUrl);

    if (!response.ok) {
      throw new Error(`Failed to load WASM file: ${response.statusText}`);
    }

    const fetchDoneAt = Date.now();
    const bytes = await response.arrayBuffer();
    const arrayBufferDoneAt = Date.now();
    const result = await WebAssembly.instantiate(bytes, go.importObject);
    const instantiateDoneAt = Date.now();
    go.run(result.instance);
    const runDoneAt = Date.now();

    const timing = {
      fetchMs: Number((fetchDoneAt - fetchStartAt).toFixed(2)),
      arrayBufferMs: Number((arrayBufferDoneAt - fetchDoneAt).toFixed(2)),
      instantiateMs: Number((instantiateDoneAt - arrayBufferDoneAt).toFixed(2)),
      goRunMs: Number((runDoneAt - instantiateDoneAt).toFixed(2)),
      totalMs: Number((runDoneAt - startAt).toFixed(2)),
    };

    this._isWasmLoaded = true;
    console.log("-----wasm 加载完成");
    console.log("[WASM][manager] timing(ms):", timing);
  }

  /**
   * 调用Go导出的签名函数
   * @param params 参数对象
   * @param path 路径
   * @param timestamp 时间戳
   * @returns 签名结果
   */
  public signParams(params, path: string, timestamp: string): string | null {
    if (!this._isWasmLoaded) {
      console.error("WASM not loaded yet");
      return null;
    }

    try {
      // 调用Go导出到window的signParams函数
      return window["signParams"](params, path, timestamp);
    } catch (err) {
      console.error("Failed to call signParams:", err);
      return null;
    }
  }

  /**
   * 确保WASM已加载，如果未加载则等待加载完成
   * @param timeoutMs 超时时间（毫秒），默认 10 秒
   * @returns Promise<void>
   */
  public async ensureLoaded(): Promise<void> {
    if (this._isWasmLoaded) return;

    if (this._loadPromise) {
      await this._loadPromise;
      return;
    }

    // 如果没有加载，尝试初始化
    await this.initWasm();
  }

  /**
   * 带超时的 WASM 加载
   * @param timeoutMs 超时时间（毫秒），默认 10 秒
   * @returns Promise<void>
   */
  public async ensureLoadedWithTimeout(timeoutMs: number = 10000): Promise<void> {
    if (this._isWasmLoaded) return;

    const timeoutPromise = new Promise<void>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`WASM 加载超时 (${timeoutMs}ms)`));
      }, timeoutMs);
    });

    const loadPromise = this.ensureLoaded();

    try {
      await Promise.race([loadPromise, timeoutPromise]);
    } catch (error) {
      console.error("WASM 加载失败:", error);
      throw error;
    }
  }

  /**
   * 检查 WASM 是否已加载
   * @returns 是否已加载
   */
  public isLoaded(): boolean {
    return this._isWasmLoaded;
  }

  /**
   * 重置管理器状态
   */
  public reset(): void {
    this._isWasmLoaded = false;
    this._loadPromise = null;
  }

  /**
   * 测试函数 - 用于验证 WASM 功能
   */
  test(): void {
    const path = "/common/api/kyc/v2/submit";

    const params = {
      account_no: "",
      account_type: "",
      action: "/common/api/kyc/v2/submit",
      branch: "",
      "cf-scene": "SCENE_GET_CODE",
      "cf-token":
        "0.ta8tJ9MSOi2ispyl7mniqp7E6FvCgoRFIZhHhNQDJaPXjLD0LiOej1WJjQ_BSBi0MdkCwlaa8wGK3WHMahg3BriJIItJFtdBnBFndLkEpBAIbrHOg70rKkB0t-SDPyAMbJxINkbYPaUP1LwzoOVuJDmjkjRsQktKeWNB4QwNvpGe0hEhvBu_PymSRTnTRXeNhbX5z9A6VMTCHiItSk-Df1HkHwOUW-iLCsHn387j-HOKlL4xpU8qUDO7E2nOgzfkvGFjhTCNgO90qJLjQO6hRPQE8WSPFthh6lvyjitTDBNJxoQWJjtgcSdn4P7sxwpMQzoJmec29u58cfoPjCP9dIAFdCKW95N6_-bg8tOcWXOf1gZrVXrUtipkdRXJPlkQuXVRJm5SqcXxPTR1k4NqhmeKhD7vswyLhtN92Dss7-dHVbd2DRM0SWArjwygmXKMv6EZuULz-aBZLJX73QcHIfKu7TFUsBS-sQL0veaSFDOhSSUkDr8O986ycQmnpR0BLoMrndj1P8_pAAdJtwnT5wa716ICUb4RgqjTv68efzsuJBG67foh7XoS4bobTb3reSPMQJbFeW_HtR7TMlmg5XH-4MTAyZoa_Pp-NX-49YjF228h4mi6JTALV9tRdpshPU48WeCDO86L_7bbcTqzhd7eSSeqF1HstX6f06RN3EAOt3F82eubXn7FjJykRdL3mg6ilZ11hHlmqHyNrxsomURlQY7GYBUXIbNHgX3TMXfS8dgzMeI3UYxiGOPv-E9mxLqk2kcYOC_rxadsRDrywcQ0qEJJV25z-6i07Uv4DOj_-JyxIjAb3P3awUSjJhJDIIA6qnP539GBCMcjW8psRtvOojroQo_8CogP8fPGbU74PMG5p5Uzg4zZyq94YpHgEzNAdvUnoDkEd3BXg7ELKpmGzBtDJIBFPB0BIYR2Jj0.nJ6I3PONW5ngvUXjGhk-1g.e9f5bf5925b670c8f59f3ea65c35e1e023075981cb5a9ac341f1b836d7b78d35",
      country: "",
      current_address: "",
      day: "1",
      first_name: "addfa",
      font_side_url: "kyc_image/1322554326350479431757499259_CdppB5Jzh8.png",
      id_type: "",
      income: "",
      is_no_goverment: "1",
      last_name: "asdfas",
      middle_name: "aa",
      month: "2",
      nationality: "",
      permanent_address: "",
      place_of_birth: "",
      selfie_picture_url: "",
      stage: 1,
      status: "Simple form",
      token:
        "Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpYXQiOjE3NTc0OTkxOTUsImV4cCI6MTc1NzU0OTU5NSwidXNlcl9pZCI6IjEzMjI1NTQzMjYzNTA0Nzk0MyIsImFwcF9wYWNrYWdlX25hbWUiOiIifQ.OehRBBTGOOrvxUdnSYRyzvWB0qHdGFeDihap6wuf1jo",
      work: "",
      year: "2000",
    };

    const timestamp = "1757493756";
    const signature = this.signParams(params, path, timestamp);
    console.log("签名结果:", signature);
  }
}
